import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Product4Legend from './Product4Legend';
import bgImage from '../assets/images/layers.png';

// Tile layer options
const tileLayers = {
    osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap contributors',
        icon: '../assets/images/StreetMap.png',
        label: 'Street Map',
    },
    terrain: {
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenTopoMap contributors',
        icon: '../assets/images/TerrainMap.png',
        label: 'Terrain',
    },
    satellite: {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri & NASA Earthstar Geographics',
        icon: '../assets/images/SatellitesMap.png',
        label: 'Satellite',
    },
};

const AnalysisMap = ({ selectedDataMap, getZoom }) => {
    const [geoJsonData, setGeoJsonData] = useState(selectedDataMap?.geojson);
    const [opacityMap, setOpacityMap] = useState({});
    const [showTileOptions, setShowTileOptions] = useState(false); // New state for toggling tile options
    const geoJsonLayerRef = useRef(null);
    const [tileLayer, setTileLayer] = useState('osm'); // Default to OpenStreetMap

    let legendData = selectedDataMap ? selectedDataMap.legend : null;

    useEffect(() => {
        if (selectedDataMap?.geojson) {
            setGeoJsonData(selectedDataMap.geojson);
            initializeOpacityMap(selectedDataMap.legend);
        }
    }, [selectedDataMap]);

    // Generate a unique key for the MapContainer when data changes
    const mapKey = useMemo(() => JSON.stringify({ geoJsonData, getZoom }), [geoJsonData, getZoom]);

    // Initialize opacity state for each legend item
    const initializeOpacityMap = (legendData) => {
        if (legendData) {
            const initialOpacity = legendData.reduce((acc, item) => {
                acc[item.color] = 0.8; // Default opacity for each legend color
                return acc;
            }, {});
            setOpacityMap(initialOpacity);
        }
    };

    // Handle opacity change from Legend component
    const handleOpacityChange = (color, newOpacity) => {
        setOpacityMap((prev) => ({
            ...prev,
            [color]: newOpacity,
        }));
    };

    // Function to update GeoJSON layer opacity dynamically
    useEffect(() => {
        if (geoJsonLayerRef.current) {
            geoJsonLayerRef.current.eachLayer((layer) => {
                const color = layer.feature.properties.color;
                if (color && opacityMap[color] !== undefined) {
                    layer.setStyle({
                        fillOpacity: opacityMap[color],
                    });
                }
            });
        }
    }, [opacityMap]);

    // Toggle tile options visibility
    const toggleTileOptions = () => {
        setShowTileOptions((prev) => !prev);
    };

    const hoverStyle = {
        color: 'red',
        weight: 1,
        fillOpacity: 1,
    };

    const onEachFeature = (feature, layer) => {
        let color;

        layer.bindTooltip(() => {
            const districtName = feature.properties.ADM2_NAME == 'Administrative unit not available' ? 'N/A' : feature.properties.ADM2_NAME || 'Unnamed District';
            const countryName = feature.properties.country || 'Unnamed Country';
            let tooltipText = `District: ${districtName} <br/> Country: ${countryName}`;
            tooltipText += `<br/> Occurrences : ${feature.properties.value}`;
            return tooltipText;
        });
        color = feature.properties.color || 'transparent';

        if (layer._path && layer._path.classList.contains('leaflet-interactive')) {
            layer.on('click', function () {
                layer.setStyle({
                    stroke: false,
                    fillOpacity: 0.8,
                    fillColor: color,
                });
            });
        }

        if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            layer.setStyle({
                color: color,
                weight: 0.5,
                fillColor: color,
                fillOpacity: 0.8,
            });

            layer.on('mouseover', function () {
                layer.setStyle({
                    ...hoverStyle,
                });
            });

            layer.on('mouseout', function () {
                layer.setStyle({
                    color: color,
                    weight: 0.5,
                    fillColor: color,
                    fillOpacity: 0.8,
                });
            });
        } else if (feature.geometry.type === 'Point') {
            layer.bindTooltip('Point feature: ' + (feature.properties.name || 'Unnamed Point'));
        }
    };

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const paths = document.querySelectorAll('path.leaflet-interactive');
            paths.forEach((path) => {
                path.style.outline = 'none';
            });
        });

        const mapContainer = document.querySelector('.leaflet-container');
        if (mapContainer) {
            observer.observe(mapContainer, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, []);

    return (
        <>
            <div className="mpPos">
                {showTileOptions && (
                    <div className="card legendCard six border-0 bg-transparent">
                        <div className="card-header bg-white px-1 pt-1 border-0 d-flex pb-1 align-items-center btnGroup">
                            {Object.entries(tileLayers).map(([key, { label, icon }]) => (
                                <button
                                    key={key}
                                    onClick={() => setTileLayer(key)}
                                    className={`btn border-0 ${tileLayer === key ? 'active' : ''}`}
                                    style={{ marginRight: '5px' }}
                                    aria-label={`Switch to ${label} map`}
                                    aria-pressed={tileLayer === key}
                                >
                                    <span>
                                        <img src={icon} loading="lazy" alt={`${label} map icon`} height="26px" />
                                    </span>{' '}
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {/* Re-renders the map when geoJsonData changes */}
                <MapContainer
                    key={mapKey}
                    center={getZoom.coordinates}
                    zoom={getZoom.zoom}
                    style={{ height: '83vh', width: '100%', marginTop: '-16px' }}
                    preferCanvas={true} // Enable canvas rendering
                >
                    <TileLayer
                        url={tileLayers[tileLayer].url}
                        attribution={tileLayers[tileLayer].attribution}
                    />

                    {geoJsonData && (
                        <GeoJSON
                            key={JSON.stringify(geoJsonData)}
                            data={geoJsonData}
                            onEachFeature={(feature, layer) => onEachFeature(feature, layer)}
                            ref={geoJsonLayerRef}
                        />
                    )}
                </MapContainer>

                {legendData && (
                    <div style={{ position: 'absolute', bottom: '100px' }}>
                        <Product4Legend legendData={legendData} onOpacityChange={handleOpacityChange} riskHazard={false} />
                    </div>
                )}
            </div>
            <div
                className="card legendCard ten border-0"
                style={{
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: '12px !important',
                    fontWeight: '600 !important',
                    position: 'absolute',
                    bottom: '12px',
                    left: '15px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent'
                }}
                onClick={toggleTileOptions}
            >
                <p className="mb-0 text-white">
                    <span><img src="../assets/images/layers1.png" /></span> Layers
                </p>
            </div>
        </>
    );
};

export default AnalysisMap;